import React, {useEffect, useMemo, useRef, useState} from 'react';

import {makeStyles} from '@material-ui/core/styles';

import {useWindowWidth} from '../../api/useWindowSize';

import styles from './style';

const useStyles = makeStyles(styles);

const getDesiredHeight = (iframe) => {
  const iFrameDoc = iframe.contentWindow.document.documentElement;
  const height = iFrameDoc.offsetHeight;
  return height + 'px';
};

const getDesiredMinWidth = (iframe) => {
  const iFrameDoc = iframe.contentWindow.document.documentElement;
  let newMinWidth = iframe.style.minWidth || '0px';

  // box width overflowing: update minWidth
  if (iFrameDoc.scrollWidth > iFrameDoc.offsetWidth + 1) {
    newMinWidth = iFrameDoc.scrollWidth+'px';
  }
  return newMinWidth;
};

const FONT_URL = process.env.PUBLIC_URL+'/contents/googleFonts.css';

const BASE_STYLES = `
<style type="text/css">
  @import url("${FONT_URL}"); 
  html { 
    box-sizing: border-box; 
    -webkit-font-smoothing: antialiased; 
    -moz-osx-font-smoothing: grayscale; 
  } 
  *, *::before, *::after { 
    box-sizing: inherit; 
  } 
  strong, b { 
    font-weight: 700; 
  } 
  @media print { 
    body { 
      background-color: #FFFFFF; 
    } 
  } 
  body::backdrop { 
    background-color: #fafafa; 
  } 
  body { 
    color: #232333; 
    margin: 0;
    margin-top: 8px;
    font-size: 0.875rem; 
    font-family: Lato,"Helvetica Neue",Roboto,sans-serif; 
    font-weight: 400; 
    line-height: 1.43; 
  } 
</style>`;

// Inject CssBaseline style rules and import and set default fonts.
const prepareSrcDoc = (srcDoc) => {
  if (srcDoc) {
    const styledSrcDoc = BASE_STYLES + srcDoc;
    return styledSrcDoc;
  }
  return srcDoc;
};

/**
 * ResponsiveIFrame Component
 * @param {Object} props
 * @param {?string} props.title iframe title
 * @param {?Object} props.style optional style
 * @param {string} props.id iframe id
 * @param {string} props.srcDoc iframe srcDoc
 * @return {ReactElement}
 */
function ResponsiveIFrame(props) {
  const classes = useStyles();

  const windowWidth = useWindowWidth();

  const iFrameRef = useRef();

  const [contentLoaded, setContentLoaded] = useState(false);
  const [additionalRefresh, setAdditionalRefresh] = useState(0);

  const preparedSrcDoc = useMemo(
      () => prepareSrcDoc(props.srcDoc),
      [props.srcDoc],
  );

  const getNewDims = () => {
    if (!iFrameRef.current) {
      return {height: '0px', minWidth: '0px'};
    }
    const newHeight = getDesiredHeight(iFrameRef.current);
    const newMinWidth = getDesiredMinWidth(iFrameRef.current);
    // console.log('new dims', newHeight, newMinWidth);
    return {height: newHeight, minWidth: newMinWidth};
  };

  const {height, minWidth} = useMemo(() => getNewDims(),
      // only recompute when the following change
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [windowWidth, contentLoaded, additionalRefresh]);

  const iFrameOnLoad = (event) => {
    if (props.srcDoc) {
      // iframe does not allow external links to open within the iframe
      // updating the links to open on a separate tab.
      const links = iFrameRef.current.contentWindow
          .document.getElementsByTagName('a');
      for (let i=0; i<links.length; i++) {
        links[i].setAttribute('target', '_blank');
      }
      // trigger re-render + recompute dims
      setContentLoaded(true);
    }
  };

  useEffect(() => {
    setContentLoaded(false);
  }, [preparedSrcDoc]);

  // trigger additional react re-render + recompute dims after content load
  // need additional re-render to finalize computed dims
  useEffect(() => {
    if (contentLoaded) {
      setAdditionalRefresh((prev) => prev+1);
    }
  }, [contentLoaded]);

  return (
    <iframe
      id={props.id}
      title={props.title}
      srcDoc={preparedSrcDoc}
      ref={iFrameRef}
      style={{
        minWidth: minWidth,
        height: height,
      }}
      className={classes.iframeContainer}
      scrolling="no"
      onLoad={iFrameOnLoad}
    />
  );
}

export default ResponsiveIFrame;
